import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from 'react-router';
import { SigninButton } from '../../components/auth/SigninButton';
import {ADMINISTRATION_LOGO_URL, ADMINISTRATION_NAME, API_URL, SYSTEM_UNION_CODE} from '../../config';
import logo from '../../assets/images/etir-na-logo.png';
import { useSelector } from 'react-redux';
import ErrorAlert from '../../components/widgets/ErrorAlert';
import SuccessAlert from '../../components/widgets/SuccessAlert';
import {useParams} from "react-router-dom";
import axios from "axios";
import {getBrowserCountryCode} from "../../utilities/BrowserHelper";
import {Button} from "react-bootstrap";
import CountrySelectModal from "../../components/Countries/CountrySelectModal";

const Home = ({ isLoggedIn }) => {

  const unionModeEnabled = Boolean(SYSTEM_UNION_CODE);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();


  const authState = useSelector((state) => state.auth);

  const routeCountryCode = useMemo(() => {
    const value = params?.countryCode;
    return value ? value.toString().toUpperCase() : '';
  }, [params]);

  const [selectedCountry, setSelectedCountry] = useState(routeCountryCode);

  const [administrationName, setAdministrationName] = useState(ADMINISTRATION_NAME || '');
  const [administrationLogoUrl, setAdministrationLogoUrl] = useState(ADMINISTRATION_LOGO_URL || '');
  const [loadingCountryDetails, setLoadingCountryDetails] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
    const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState('');
  const [autoRedirectAttempted, setAutoRedirectAttempted] = useState(false);
  // if we're logged in, then redirect to dashboard

  useEffect(() => {
    if (isLoggedIn)
      navigate("/app/dashboard");
  }, [navigate, isLoggedIn]);

  useEffect(() => {
    setSelectedCountry(routeCountryCode);
  }, [routeCountryCode]);

  useEffect(() => {
    if (!unionModeEnabled) {
      setCountries([]);
      setCountriesError('');
      setCountriesLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadCountries = async () => {
      try {
        setCountriesLoading(true);
        setCountriesError('');
        const { data } = await axios.get(`${API_URL}/country/countriesByUnion/${SYSTEM_UNION_CODE}`, {
          signal: controller.signal,
        });

        if (Array.isArray(data)) {
          setCountries(data);
        } else {
          setCountries([]);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Unable to load countries for union', error);
          setCountriesError('loadFailed');
        }
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    };

    loadCountries();

    return () => controller.abort();
  }, [unionModeEnabled]);

  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => {
      const nameA = (a?.customsAdministrationName || a?.isoCode2 || '').toLowerCase();
      const nameB = (b?.customsAdministrationName || b?.isoCode2 || '').toLowerCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }, [countries]);


  useEffect(() => {
    const resetAdministrationDetails = () => {
      setAdministrationName(ADMINISTRATION_NAME || '');
      setAdministrationLogoUrl(ADMINISTRATION_LOGO_URL || '');
      setLoadingCountryDetails(false);
    };

    if (!unionModeEnabled) {
      resetAdministrationDetails();
      return undefined;
    }

    const countryCode = selectedCountry ? selectedCountry.toLowerCase() : '';
    if (!countryCode) {
      resetAdministrationDetails();
      return undefined;
    }

    const controller = new AbortController();

    const loadCountryDetails = async () => {
      try {
        setLoadingCountryDetails(true);
        const { data } = await axios.get(`${API_URL}/country/${countryCode}`, {
          signal: controller.signal,
        });

        if (data) {
          setAdministrationName(data.customsAdministrationName || ADMINISTRATION_NAME || '');
          setAdministrationLogoUrl(data.customsAdministrationLogoUrl || ADMINISTRATION_LOGO_URL || '');
        } else {
          resetAdministrationDetails();
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(`Unable to load country details for ${countryCode}`, error);
        }
        resetAdministrationDetails();
      } finally {
        setLoadingCountryDetails(false);
      }
    };

    loadCountryDetails();

    return () => controller.abort();
  }, [selectedCountry, unionModeEnabled]);

  const handleCountrySelected = useCallback((countryCode) => {
    const normalized = countryCode ? countryCode.toUpperCase() : '';

    setSelectedCountry(normalized);

    if (!normalized) {
      navigate('/login');
      return;
    }

    navigate(`/login/${normalized.toLowerCase()}`);
  }, [navigate]);

  useEffect(() => {
    if (!unionModeEnabled || routeCountryCode || autoRedirectAttempted) {
      return;
    }

    if (countriesLoading || ! sortedCountries.length) {
      return;
    }

    const browserCountry = getBrowserCountryCode();

    if (browserCountry) {
      const match = sortedCountries.find((country) => country?.isoCode2?.toUpperCase() === browserCountry.toUpperCase());

      if (match) {
        handleCountrySelected(match.isoCode2);
        setAutoRedirectAttempted(true);
        return;
      }
    }

    setAutoRedirectAttempted(true);
  }, [autoRedirectAttempted, countriesLoading, handleCountrySelected, routeCountryCode, sortedCountries, unionModeEnabled]);


  return (

    <section className="py-0">
      <div className="row flex-center  justify-content-center min-vh-100 xvw-100 py-6">
        <div className="col-sm-10 col-md-8 col-lg-6 col-xl-6">

          <div className="card mt-5" id="authCard">
            <div className="card-body fs--1 font-weight-normal p-5">
              <div className="row justify-content-center row-cols-1 row-cols-sm-1 align-items-center mb-3">
                <div className="col text-center">
                  <h2>{t("auth:hdgHeading")}</h2>
                  {!loadingCountryDetails && administrationLogoUrl && (
                    <img
                      src={administrationLogoUrl}
                      alt={t("footer:imgAltETir")}
                      className="img-fluid px-3 m-5"
                      style={{ maxHeight: 300 }}
                    />
                  )}
                  {!loadingCountryDetails && administrationName && (
                    <h2 className='mt-3'>{administrationName}</h2>
                  )}
                </div>
              </div>


              <div className='my-5 text-center'>
                
                <img id="eTIRLogo" src={logo} height="100px" alt="eTIR National Application" className='my-5'/>
                {authState.error && <ErrorAlert error={t('auth:error:' + authState.error)}/> }
                {location?.state?.to && <div className='mx-5'><SuccessAlert persistant message={t('auth:msgAutoLogout')} /></div>}
                <SigninButton countryCode={selectedCountry} onCountrySelected={handleCountrySelected}/>

                {unionModeEnabled && (
                  <div className="mt-3">
                    <Button
                      variant="link"
                      className="p-0"
                      onClick={() => setShowCountryModal(true)}
                    >
                      {t('auth:btnChangeCountry')}
                    </Button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
      <CountrySelectModal
        show={showCountryModal}
        onHide={() => setShowCountryModal(false)}
        countries={sortedCountries}
        loading={countriesLoading}
        error={countriesError}
        defaultCountryCode={selectedCountry}
        onSelect={(countryCode) => {
          handleCountrySelected(countryCode);
          setShowCountryModal(false);
        }}
      />
    </section>
  );

};

export default Home;